import { useState, useMemo } from 'react';
import { Button, Form, Modal, Container, Row, Col } from 'react-bootstrap';
import Loader from '../components/Loader';
import PageHeader from '../components/PageHeader';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom'; 
import { useLazyGetProductDetailsQuery } from "../slices/productsApiSlice";
import { addToCart } from "../slices/cartSlice";
import { isValidIsbn, isbn10to13 } from '../utils/validation';
import AlertModal from '../components/AlertModal';
import { toast } from 'react-toastify';

const HomeScreen = () => {
  
const { userInfo } = useSelector((state) => state.auth);

const [barcode, setBarcode] = useState('') 
const [triggerBarcode, setTriggerBarcode] = useState(null); // this triggers the fetch
const [requestPending, setRequestPending] = useState(false);
const [requestError, setRequestError] = useState('');
const [showModal, setShowModal] = useState(false);
const [alertModal, setAlertModal] = useState({
    show: false,
    message: "",
    variant: "danger",
    });

const showAlert = (message, variant = "danger") => {
  setAlertModal({ show: true, message, variant });
};

const clearSearch = () => {
    setBarcode('');
    setTriggerBarcode(null);
    setRequestError('');
};
  
const cleanedBarcode = barcode?.trim();

const [fetchProductDetails, { currentData: offer, isFetching, isLoading }] = useLazyGetProductDetailsQuery();

const navigate = useNavigate(); 
const dispatch = useDispatch();
const { cartItems } = useSelector((state) => state.cart);

const sectionStyle = {
height: '30vh',
display: 'flex',
flexDirection: 'column',
justifyContent: 'space-around',
alignItems: 'center',
textAlign: 'center',
padding: '20px',
borderRadius: '8px',
boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
backgroundColor: '#f8f9fa'
};

    const finalOffer = useMemo(() => {
    if (!offer || !triggerBarcode || !isValidIsbn(triggerBarcode)) return null;
    return { ...offer, isbn: triggerBarcode };
    }, [offer, triggerBarcode]);

    const handleSearchClick = () => {
        let isbnToSearch = cleanedBarcode;

        // Auto-convert ISBN-10 to ISBN-13
        if (isbnToSearch.length === 10) {
            const converted = isbn10to13(isbnToSearch);
            if (!converted) {
            showAlert("Ongeldig ISBN-10 nummer.", "warning");
            return;
            }
            isbnToSearch = converted;
        }

        const valid = isValidIsbn(isbnToSearch) && isbnToSearch.length === 13;

        if (!valid) {
        showAlert("Voer een geldige ISBN in van 13 cijfers.", "warning");
        return;
        }

        // 4th digit rule
        if (isbnToSearch[3] !== '9') {
            showAlert("Sorry, we can't buy this book.", "warning");
            return;
        }

        if (cartItems.some((item) => item.isbn === isbnToSearch)) {
            clearSearch();
            toast.info('Dit boek staat al in uw winkelwagen.');
            return;
        }

        setTriggerBarcode(isbnToSearch);
        setRequestError('');
        setRequestPending(true);
        setShowModal(true);
        fetchProductDetails(isbnToSearch, false)
            .unwrap()
            .catch((err) => {
                setRequestError(err?.data?.message || err.error || 'Fout bij het ophalen van gegevens.');
            })
            .finally(() => setRequestPending(false));
    };

const handleBarcodeScan = () => {
navigate('/barcode');  
}

const handleModalClose = () => {
    setShowModal(false);
    clearSearch();
};

const addToCartHandler = () => {
    if (!finalOffer || !finalOffer.isbn) {
    showAlert("Ongeldig item", "warning");
    setShowModal(false);
    return;
    }
    const isInCart = cartItems.some((x) => x.isbn === finalOffer.isbn);
    if (isInCart) {
        toast.info('Dit boek staat al in uw winkelwagen.');
        setShowModal(false);
        clearSearch();
    } else {
        dispatch(addToCart(finalOffer));
        setShowModal(false);
        clearSearch();
        toast.success('Boek toegevoegd aan de winkelwagen.');
    }
};

  return (
    <>
        { isLoading ? (
            <Loader />
        ) : (
            <div>
                <PageHeader 
                    totalBooks={cartItems.length}
                    totalWeight={0}
                    totalPrice={cartItems.reduce((acc, item) => acc + parseFloat(item.offer_price || 0), 0).toFixed(2)} 
                />

                <Container className="py-5">               
                    {/* Section 1: Barcode Search */}
                    <Row className="justify-content-center mb-4">
                    <Col xs={12} md={9} lg={6}>
                        <div style={sectionStyle}>
                        <h3>Zoeken op streepjescode</h3>
                        <Row>
                        <Col xs={12} lg={7} className='mx-auto'>
                            <p  className="fs-6 fs-lg-5">
                            Voeg een boek toe door de streepjescode te scannen, of door ISBN nummer te typen.
                            </p>
                        </Col>
                        </Row>
                        <Button 
                            variant="primary" 
                            className="px-5 text-white"
                            onClick={handleBarcodeScan}
                        >
                            Scan ISBN
                        </Button>
                        </div>
                    </Col>
                    </Row>

                    {/* Section 2: ISBN Search */}
                    <Row className="justify-content-center">
                    {/* <Notification message={alertMessage} setMessage={setAlertMessage} /> */}
                    <AlertModal
                        show={alertModal.show}
                        onClose={() => {
                            setAlertModal({ ...alertModal, show: false });
                            clearSearch();
                        }}
                        message={alertModal.message}
                        variant={alertModal.variant}
                        />

                    <Col xs={12} md={9} lg={6}>
                        <div style={sectionStyle}>
                        <h3>Zoek op ISBN nummer</h3>
                        <Form 
                            className="mt-3" 
                            onSubmit={(e) => {
                            e.preventDefault();
                            handleSearchClick();
                        }}>
                            <Form.Group controlId="isbnSearch" className="mb-3">
                            <Form.Control
                                type="text"
                                placeholder="Typ hier een ISBN nummer"
                                className="text-center p-3 border border-primary shadow-sm"
                                value={barcode}
                                onChange={(e) => {
                                setBarcode(e.target.value);
                                if (triggerBarcode) setTriggerBarcode(null); // <— cancel previous search
                                setRequestError('');
                            }}
                            />
                            </Form.Group>
                            {/* <Form.Check
                                type="checkbox"
                                label="Mijn ISBN nummer eindigt op X"
                                className="mb-3 d-flex gap-2 align-items-start"
                            /> */}
                            <Button
                                variant="primary"
                                type="submit"
                                className="px-5 text-white"
                                // onClick={handleSearchClick}
                                disabled={!barcode.trim()}
                            >
                            Zoeken
                            </Button>
                        </Form>

                        {/* Modal */}
                        <Modal show={showModal} onHide={() => handleModalClose()}>
                            <Modal.Header closeButton>
                            <Modal.Title>Toevoegen</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                            {requestPending || isFetching ? (
                                <div className="text-center py-4">
                                    <Loader />
                                </div>
                            ) : requestError ? (
                                <p className="text-danger">{requestError}</p>
                            ) : finalOffer ? (
                                <div>
                                    <h5><strong>Title:</strong> {finalOffer.title || "Onbekend"}</h5>
                                    <p><strong>ISBN:</strong> {finalOffer.isbn || "Onbekend"}</p>
                                    <p><strong>Author:</strong> {finalOffer.author || "Onbekend"}</p>
                                    {
                                        finalOffer && finalOffer?.offer_price > 0.00 ? 
                                        <>
                                            <p className='text-center'>Our offer:</p>
                                            <span className='text-center'>
                                                <h4><strong> €{finalOffer.offer_price}</strong></h4>
                                            </span>
                                        </>
                                        :
                                        <p className='text-center'><strong>Sorry, we can't buy it.</strong></p>
                                    }
                                    {
                                        userInfo && userInfo.isAdmin ? (
                                            <>
                                                <p><strong>Source:</strong> {finalOffer.source}</p>
                                                <p><strong>Message:</strong> {finalOffer.message}</p>
                                            </>
                                        ) : null
                                    }
                                </div>
                            ) : (
                                <p>Geen gegevens gevonden.</p>
                            )}
                            </Modal.Body>
                            <Modal.Footer>
                            <Button
                            variant="danger"
                            onClick={() => handleModalClose()}
                            style={{ backgroundColor: "#6c757d", borderColor: "#6c757d", color: "white" }}
                            >
                                {!requestPending && !isFetching && !requestError && finalOffer?.offer_price > 0.00 ? "Annuleren" : "Sluiten"}
                            </Button>
                            {!requestPending && !isFetching && !requestError && finalOffer?.offer_price > 0.00 &&
                            <Button
                            variant="success"
                            style={{ backgroundColor: "#28a745", borderColor: "#28a745", color: "white" }}
                            onClick={addToCartHandler}
                            >
                            Toevoegen
                            </Button>
                            }
                            </Modal.Footer>
                        </Modal>
                        </div>
                    </Col>
                    </Row>             
                </Container>
                </div>
        )}
    </>
  );
};

export default HomeScreen;
